import React, { useEffect, useMemo, useState, useRef } from "react";
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import "jspdf-autotable";
import dayjs from "dayjs";
import Swal from "sweetalert2";
import { FaCog } from "react-icons/fa";
import { postData } from "../../services/FetchNodeservices";

export default function InterviewReport() {
  const [filters, setFilters] = useState({
    search: "",
    date: "",
    customFrom: "",
    customTo: "",
    company: "",
    position: "",
    stage: "",
  });

  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);

  const [page, setPage] = useState(0);
  const [rowsPerPage] = useState(10);

  const [columns, setColumns] = useState({
    candidate: true,
    company: true,
    position: true,
    date: true,
    time: true,
    stage: true,
  });
  const [openColumns, setOpenColumns] = useState(false);
  const columnRef = useRef();

  useEffect(() => {
    const handler = (e) => {
      if (columnRef.current && !columnRef.current.contains(e.target)) {
        setOpenColumns(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  /* ================= FETCH ================= */
  useEffect(() => {
    fetchAll();
  }, []);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const userData = JSON.parse(localStorage.getItem("USER")) || {};
      const res = await postData("interview/fetch", {
        user_id: userData.id,
      });

      if (res.status) {
        setData(Array.isArray(res.data) ? res.data : []);
      } else {
        setData([]);
        Swal.fire("Error", res.message || "Failed to fetch", "error");
      }
    } catch {
      Swal.fire("Error", "Server error", "error");
    } finally {
      setLoading(false);
    }
  };

  /* ================= DYNAMIC OPTIONS ================= */
  const companyOptions = useMemo(() => {
    return [...new Set(data.map((r) => r.company).filter(Boolean))].sort();
  }, [data]);

  const positionOptions = useMemo(() => {
    return [...new Set(data.map((r) => r.position).filter(Boolean))].sort();
  }, [data]);

  const stageOptions = useMemo(() => {
    return [...new Set(data.map((r) => r.stage).filter(Boolean))].sort();
  }, [data]);

  /* ================= FILTER ================= */
  const filteredData = useMemo(() => {
    return data.filter((r) => {
      const searchMatch = !filters.search || Object.values(r)
        .filter(Boolean)
        .some((val) => String(val).toLowerCase().includes(filters.search.toLowerCase()));

      if (!searchMatch) return false;

      if (filters.company && r.company !== filters.company) return false;
      if (filters.position && r.position !== filters.position) return false;
      if (filters.stage && r.stage !== filters.stage) return false;

      if (!filters.date) return true;

      const d = dayjs(r.interview_date);
      const today = dayjs();

      let dateMatch = true;

      if (filters.date === "today") {
        dateMatch = d.isSame(today, "day");
      } else if (filters.date === "lastWeek") {
        dateMatch = d.isAfter(today.subtract(7, "day"));
      } else if (filters.date === "lastMonth") {
        dateMatch = d.isAfter(today.subtract(1, "month"));
      } else if (filters.date === "custom") {
        const fromOK = !filters.customFrom || d.isAfter(dayjs(filters.customFrom).subtract(1, "day"));
        const toOK = !filters.customTo || d.isBefore(dayjs(filters.customTo).add(1, "day"));
        dateMatch = fromOK && toOK;
      }

      return dateMatch;
    });
  }, [data, filters]);

  const paginatedData = filteredData.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  const totalPages = Math.ceil(filteredData.length / rowsPerPage);

  /* ================= EXPORT ================= */
  const exportExcel = () => {
    const rows = filteredData.map((r) => {
      const obj = {};
      if (columns.candidate) obj["Candidate"] = r.candidate_name || "";
      if (columns.company) obj["Company"] = r.company || "";
      if (columns.position) obj["Position"] = r.position || "";
      if (columns.date) obj["Date"] = r.interview_date ? dayjs(r.interview_date).format("DD-MM-YYYY") : "";
      if (columns.time) obj["Time"] = r.interview_time || "";
      if (columns.stage) obj["Stage"] = r.status || "";
      return obj;
    });

    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Interview");
    XLSX.writeFile(wb, "interview.xlsx");
  };

  const exportPDF = () => {
    const doc = new jsPDF();
    doc.text("Interview Report", 14, 10);

    doc.autoTable({
      head: [["Date", "Time", "Stage", "Candidate", "Status"]],
      body: filteredData.map((r) => [
        dayjs(r.interview_date).format("DD-MM-YYYY"),
        r.interview_time,
        r.status,
        r.candidate_name,
        r.interview_status,
      ]),
    });

    doc.save("interview.pdf");
  };

  /* ================= STATUS COLOR ================= */
  const getStatusColor = (status) => {
    if (status === "Interview L1") return "bg-blue-100 text-blue-700";
    if (status === "Interview L2") return "bg-purple-100 text-purple-700";
    if (status === "Face to Face") return "bg-green-100 text-green-700";
    return "bg-gray-100 text-gray-700";
  };

  const clearFilters = () => {
    setFilters({
      search: "",
      date: "",
      customFrom: "",
      customTo: "",
      company: "",
      position: "",
      stage: "",
    });
    setPage(0);
  };

  return (
    <div className="w-full space-y-4">
      {/* Header */}
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h2 className="text-base font-extrabold text-gray-800 uppercase tracking-wider">
            Interview Tracker
          </h2>
          <div className="mt-1 flex items-center gap-2 text-[10px] font-bold uppercase tracking-wider">
            <span className="inline-flex items-center rounded-full bg-gray-100 px-2 py-0.5 text-gray-600">
              Total: {filteredData.length}
            </span>
          </div>
        </div>

        <div className="flex gap-2">
          <button
            onClick={clearFilters}
            className="px-4 py-2 border border-gray-200 text-xs font-bold text-gray-650 hover:bg-gray-50 rounded-xl transition duration-200 active:scale-[0.99]"
          >
            Clear
          </button>

          <button
            onClick={exportExcel}
            className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-bold rounded-xl shadow-lg shadow-indigo-100 transition duration-200 active:scale-[0.99]"
          >
            Export Excel 📄
          </button>

          {/* COLUMNS SELECTION BUTTON */}
          <div className="relative" ref={columnRef}>
            <button
              onClick={() => setOpenColumns((p) => !p)}
              className="px-4 py-2 bg-white border border-gray-200 text-xs font-bold text-gray-700 rounded-xl shadow-sm hover:bg-gray-50 transition duration-200 active:scale-[0.99] flex items-center gap-2"
            >
              <FaCog /> Columns
            </button>

            {openColumns && (
              <div className="absolute right-0 mt-2 bg-white p-4 border border-gray-100 rounded-2xl shadow-xl w-60 z-50 max-h-72 overflow-y-auto animate-fadeIn">
                {Object.keys(columns).map((c) => (
                  <label key={c} className="flex gap-2 text-xs py-1.5 cursor-pointer hover:bg-gray-50 items-center font-semibold text-gray-700 capitalize">
                    <input
                      type="checkbox"
                      checked={columns[c]}
                      className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                      onChange={() =>
                        setColumns((prev) => ({ ...prev, [c]: !prev[c] }))
                      }
                    />
                    {c}
                  </label>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* FILTER PANEL */}
      <div className="border border-gray-100 rounded-2xl overflow-visible bg-white shadow-sm min-h-[480px]">
        <div className="border-b border-gray-100 bg-gray-50/20 p-4 relative z-30">
          <div className="flex flex-wrap gap-3 items-center">
            <input
              placeholder="Search candidate / company..."
              className="h-10 w-full sm:w-72 px-3 bg-white border border-gray-200 rounded-xl text-xs font-semibold text-gray-700 placeholder-gray-400 focus:outline-none focus:ring-4 focus:ring-indigo-100/80 focus:border-indigo-500 transition duration-200"
              value={filters.search}
              onChange={(e) => {
                setFilters((p) => ({ ...p, search: e.target.value }));
                setPage(0);
              }}
            />

            <select
              className="h-10 px-3 bg-white border border-gray-200 rounded-xl text-xs font-semibold text-gray-700 focus:outline-none focus:ring-4 focus:ring-indigo-100/80 focus:border-indigo-500 transition duration-200 cursor-pointer"
              value={filters.company}
              onChange={(e) => {
                setFilters((p) => ({ ...p, company: e.target.value }));
                setPage(0);
              }}
            >
              <option value="">All Companies</option>
              {companyOptions.map((c) => (
                <option key={c} value={c}>{c}</option>
              ))}
            </select>

            <select
              className="h-10 px-3 bg-white border border-gray-200 rounded-xl text-xs font-semibold text-gray-700 focus:outline-none focus:ring-4 focus:ring-indigo-100/80 focus:border-indigo-500 transition duration-200 cursor-pointer"
              value={filters.position}
              onChange={(e) => {
                setFilters((p) => ({ ...p, position: e.target.value }));
                setPage(0);
              }}
            >
              <option value="">All Positions</option>
              {positionOptions.map((pos) => (
                <option key={pos} value={pos}>{pos}</option>
              ))}
            </select>

            <select
              className="h-10 px-3 bg-white border border-gray-200 rounded-xl text-xs font-semibold text-gray-700 focus:outline-none focus:ring-4 focus:ring-indigo-100/80 focus:border-indigo-500 transition duration-200 cursor-pointer"
              value={filters.stage}
              onChange={(e) => {
                setFilters((p) => ({ ...p, stage: e.target.value }));
                setPage(0);
              }}
            >
              <option value="">All Stages</option>
              {stageOptions.map((stg) => (
                <option key={stg} value={stg}>{stg}</option>
              ))}
            </select>

            <select
              className="h-10 px-3 bg-white border border-gray-200 rounded-xl text-xs font-semibold text-gray-700 focus:outline-none focus:ring-4 focus:ring-indigo-100/80 focus:border-indigo-500 transition duration-200 cursor-pointer"
              value={filters.date}
              onChange={(e) => {
                setFilters((p) => ({ ...p, date: e.target.value }));
                setPage(0);
              }}
            >
              <option value="">Any Date</option>
              <option value="today">Today</option>
              <option value="lastWeek">Last Week</option>
              <option value="lastMonth">Last Month</option>
              <option value="custom">Custom</option>
            </select>

            {filters.date === "custom" && (
              <div className="flex gap-2">
                <input
                  type="date"
                  value={filters.customFrom}
                  onChange={(e) => {
                    setFilters((p) => ({ ...p, customFrom: e.target.value }));
                    setPage(0);
                  }}
                  className="h-10 px-3 bg-white border border-gray-200 rounded-xl text-xs font-semibold text-gray-700 focus:outline-none focus:ring-4 focus:ring-indigo-100/80 focus:border-indigo-500 transition duration-200"
                />
                <input
                  type="date"
                  value={filters.customTo}
                  onChange={(e) => {
                    setFilters((p) => ({ ...p, customTo: e.target.value }));
                    setPage(0);
                  }}
                  className="h-10 px-3 bg-white border border-gray-200 rounded-xl text-xs font-semibold text-gray-700 focus:outline-none focus:ring-4 focus:ring-indigo-100/80 focus:border-indigo-500 transition duration-200"
                />
              </div>
            )}
          </div>
        </div>

        {/* TABLE */}
        <div className="overflow-x-auto">
          <table className="min-w-full text-xs">
            <thead>
              <tr className="border-b border-gray-150 bg-gray-50/50 text-[10px] font-bold text-gray-500 uppercase tracking-wider text-left">
                <th className="px-4 py-3.5 w-12">Sr</th>
                {columns.candidate && <th className="px-4 py-3.5">Candidate</th>}
                {columns.company && <th className="px-4 py-3.5">Company</th>}
                {columns.position && <th className="px-4 py-3.5">Position</th>}
                {columns.date && <th className="px-4 py-3.5">Date</th>}
                {columns.time && <th className="px-4 py-3.5">Time</th>}
                {columns.stage && <th className="px-4 py-3.5">Stage</th>}
              </tr>
            </thead>

            <tbody className="divide-y divide-gray-100">
              {loading ? (
                <tr>
                  <td colSpan={7} className="p-8 text-center text-gray-400 font-semibold">
                    Loading...
                  </td>
                </tr>
              ) : paginatedData.length === 0 ? (
                <tr>
                  <td colSpan={7} className="py-20 text-center">
                    <div className="flex flex-col items-center justify-center gap-2 max-w-sm mx-auto">
                      <div className="w-12 h-12 rounded-2xl bg-indigo-50/80 flex items-center justify-center text-indigo-500 text-xl mb-1">
                        📅
                      </div>
                      <p className="text-sm font-bold text-gray-700">No Interview Records Found</p>
                      <p className="text-xs text-gray-400 font-medium">No interview logs match your selected filter criteria.</p>
                    </div>
                  </td>
                </tr>
              ) : (
                paginatedData.map((row, i) => (
                  <tr key={row.id} className="hover:bg-indigo-50/10 transition duration-150 text-gray-700">
                    <td className="px-4 py-3 font-semibold text-gray-500">
                      {page * rowsPerPage + i + 1}
                    </td>

                    {columns.candidate && (
                      <td className="px-4 py-3 font-bold text-gray-900 whitespace-nowrap">
                        {row.candidate_name || "-"}
                      </td>
                    )}

                    {columns.company && (
                      <td className="px-4 py-3 text-gray-600 whitespace-nowrap">
                        {row.company || "-"}
                      </td>
                    )}

                    {columns.position && (
                      <td className="px-4 py-3 font-semibold text-gray-800 whitespace-nowrap">
                        {row.position || "-"}
                      </td>
                    )}

                    {columns.date && (
                      <td className="px-4 py-3 whitespace-nowrap font-medium text-gray-500">
                        {row.interview_date
                          ? dayjs(row.interview_date).format("DD MMM YYYY")
                          : "-"}
                      </td>
                    )}

                    {columns.time && (
                      <td className="px-4 py-3 whitespace-nowrap">
                        {row.interview_time
                          ? dayjs(`2000-01-01 ${row.interview_time}`).format("hh:mm A")
                          : "-"}
                      </td>
                    )}

                    {columns.stage && (
                      <td className="px-4 py-3 whitespace-nowrap">
                        <span
                          className={`px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider ${
                            row.stage === "Interview L1"
                              ? "bg-blue-50 text-blue-700 ring-1 ring-blue-600/10"
                              : row.stage === "Interview L2"
                              ? "bg-purple-50 text-purple-700 ring-1 ring-purple-600/10"
                              : row.stage === "Face to Face"
                              ? "bg-emerald-50 text-emerald-700 ring-1 ring-emerald-600/10"
                              : "bg-gray-50 text-gray-700 ring-1 ring-gray-600/10"
                          }`}
                        >
                          {row.stage || "-"}
                        </span>
                      </td>
                    )}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* PAGINATION */}
        <div className="border-t border-gray-150 px-4 py-3 flex items-center justify-between bg-white text-xs">
          <div className="text-gray-500 font-medium">
            Showing <span className="font-semibold text-gray-800">
              {filteredData.length === 0 ? 0 : page * rowsPerPage + 1}
            </span>–
            <span className="font-semibold text-gray-800">
              {Math.min((page + 1) * rowsPerPage, filteredData.length)}
            </span> of{" "}
            <span className="font-semibold text-gray-800">{filteredData.length}</span>
          </div>

          <div className="flex items-center gap-2">
            <button
              disabled={page === 0}
              onClick={() => setPage((p) => p - 1)}
              className="px-3 py-1.5 border border-gray-200 rounded-xl text-xs font-bold text-gray-650 disabled:opacity-40 disabled:cursor-not-allowed hover:bg-gray-50 active:scale-[0.99] transition duration-200"
            >
              Prev
            </button>
            <button
              disabled={page + 1 >= totalPages}
              onClick={() => setPage((p) => p + 1)}
              className="px-3 py-1.5 border border-gray-200 rounded-xl text-xs font-bold text-gray-650 disabled:opacity-40 disabled:cursor-not-allowed hover:bg-gray-50 active:scale-[0.99] transition duration-200"
            >
              Next
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}